import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, Fuel, Clock, Database, Cpu, ArrowRight } from "lucide-react";
import { SOLUTIONS, IMAGES } from "@/data/content";

const ICONS = { Zap, Fuel, Clock, Database, Cpu };

const HEADLINE_OVERRIDES = {
  "04": (
    <>
      Store renewable energy instead
      <br className="hidden lg:block" /> of wasting it
    </>
  ),
};

const SolutionItem = ({ item, className = "", bodyClassName = "max-w-md" }) => {
  const Icon = ICONS[item.icon];

  return (
    <div data-testid={`solution-item-${item.n}`} className={className}>
      <div className="flex items-center gap-2.5">
        <Icon size={15} className="text-teal" strokeWidth={1.8} aria-hidden="true" />
        <span className="font-mono text-xs text-teal">{item.n}</span>
        <span
          data-testid={`solution-item-title-${item.n}`}
          className="text-xs font-medium uppercase tracking-[0.12em] text-inkMuted"
        >
          {item.title}
        </span>
      </div>

      <h3
        data-testid={`solution-item-headline-${item.n}`}
        className="mt-4 font-heading text-2xl font-semibold leading-snug text-ink lg:text-[28px]"
      >
        {HEADLINE_OVERRIDES[item.n] || item.headline}
      </h3>

      <p data-testid={`solution-item-body-${item.n}`} className={`mt-4 text-[15px] leading-relaxed text-inkSoft ${bodyClassName}`}>
        {item.body}
      </p>

      {item.supportingLine && (
        <p data-testid={`solution-item-supporting-${item.n}`} className="mt-3 text-sm text-inkMuted">
          {item.supportingLine}
        </p>
      )}

      {item.stat && (
        <div data-testid={`solution-item-stat-${item.n}`} className="mt-7">
          <span className="block font-heading text-4xl font-semibold text-teal sm:text-5xl">{item.stat.value}</span>
          <span className="mt-1.5 block text-sm text-inkMuted">{item.stat.label}</span>
        </div>
      )}
      {item.disclaimer && (
        <p
          data-testid={`solution-item-disclaimer-${item.n}`}
          className="mt-3 max-w-md text-xs italic leading-relaxed text-inkMuted lg:max-w-none lg:whitespace-nowrap"
        >
          {item.disclaimer}
        </p>
      )}

      {item.link && (
        <Link
          to={item.link.to}
          data-testid={`solution-item-link-${item.n}`}
          className="group mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-tealHover transition-colors duration-300"
        >
          {item.link.label}
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
};

export const SolutionsList = () => {
  const [s1, s2, s3, s4, s5] = SOLUTIONS;

  return (
    <section data-testid="solutions-list-section" className="bg-baseLight px-6 pt-[20px] pb-[30px] sm:py-[40px] lg:px-12 lg:py-[70px]">
      <div className="lg:mx-auto lg:max-w-[1280px]">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-16 sm:divide-x sm:divide-borderLight lg:gap-24"
        >
          <SolutionItem item={s1} bodyClassName="max-w-md lg:max-w-[544px]" />
          <SolutionItem item={s2} className="sm:pl-8 lg:pl-12" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          data-testid="solutions-list-break-image"
          className="mt-[51px] mb-14 h-[280px] overflow-hidden rounded-3xl sm:h-[340px] lg:mt-20 lg:mb-20 lg:h-[374px]"
        >
          <img
            src={IMAGES.hillsideSolarArray}
            alt="A hillside solar panel array overlooking a forested valley and mountains"
            className="h-full w-full object-cover"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 gap-12 pt-[4px] sm:grid-cols-2 sm:gap-16 sm:divide-x sm:divide-borderLight lg:gap-24 lg:border-t lg:border-borderLight lg:pt-16"
        >
          <SolutionItem item={s3} bodyClassName="max-w-md lg:max-w-xl" />
          <SolutionItem item={s4} className="sm:pl-8 lg:pl-12" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mt-[14px] pt-[41px] lg:mt-16 lg:border-t lg:border-borderLight lg:pt-16"
        >
          <SolutionItem item={s5} className="max-w-2xl lg:max-w-[736px]" bodyClassName="max-w-md lg:max-w-[704px]" />
        </motion.div>
      </div>
    </section>
  );
};
